<template>
  <div class="coach-page">
    <!-- ===== حالت لودینگ ===== -->
    <div v-if="loading" class="state-box">
      <div class="spinner"></div>
      <p>در حال دریافت اطلاعات مربی...</p>
    </div>

    <!-- ===== خطا ===== -->
    <div v-else-if="error" class="state-box error">
      <i class="fas fa-exclamation-circle"></i>
      <p>{{ error }}</p>
      <router-link to="/" class="back-btn">بازگشت به خانه</router-link>
    </div>

    <div v-else-if="coach" class="coach-container">

      <!-- breadcrumb -->
      <div class="breadcrumb">
        <router-link to="/">خانه</router-link>
        <i class="fas fa-chevron-left"></i>
        <router-link v-if="team" :to="`/team/${team.slug}`">{{ faTeamName(team.name) }}</router-link>
        <template v-if="team"><i class="fas fa-chevron-left"></i></template>
        <span>{{ faName }}</span>
      </div>

      <!-- ===== کارت پروفایل ===== -->
      <div class="coach-hero">
        <div class="photo-wrap">
          <!-- جای عکس: فایل را در src/assets/image/coach/{slug}.jpg قرار دهید -->
          <img v-if="photo" :src="photo" class="coach-photo" :alt="coach.name"/>
          <span v-else class="coach-avatar" :style="{ background: avatarColor }">
            <i class="fas fa-user-tie"></i>
          </span>
        </div>

        <div class="hero-info">
          <h1 class="coach-name">{{ faName }}</h1>
          <div class="coach-meta">
            <span class="meta-item"><i class="fas fa-clipboard-list"></i> سرمربی</span>
            <span v-if="nationality" class="meta-item">
              <img v-if="flag" :src="flag" class="flag"/>
              {{ faCountry }}
            </span>
            <span v-if="age" class="meta-item">
              <i class="fas fa-birthday-cake"></i> {{ faNum(age) }} ساله
            </span>
          </div>
        </div>

        <router-link v-if="team" :to="`/team/${team.slug}`" class="team-chip">
          <img v-if="team.logo" :src="team.logo"/>
          <span>{{ faTeamName(team.name) }}</span>
          <i class="fas fa-chevron-left"></i>
        </router-link>
      </div>

      <!-- ===== بازی‌های اخیر تیم ===== -->
      <div class="section-box" v-if="teamRecent.length">
        <div class="section-title"><i class="fas fa-futbol"></i> بازی‌های اخیر {{ faTeamName(team?.name) }}</div>
        <div class="matches-list">
          <article
              v-for="m in teamRecent"
              :key="m.id"
              class="match-row"
              @click="$router.push(`/match/${m.id}`)"
          >
            <span class="comp">{{ faLeagueName(m.competition?.name) }}</span>
            <div class="teams">
              <span class="t" :class="{ winner: m.home?.isWinner }">{{ faTeamName(m.home?.team?.name) }}</span>
              <strong class="score">{{ m.home?.score ?? 0 }} - {{ m.away?.score ?? 0 }}</strong>
              <span class="t" :class="{ winner: m.away?.isWinner }">{{ faTeamName(m.away?.team?.name) }}</span>
            </div>
            <span class="date">{{ faDate(m.kickoffAt) }}</span>
          </article>
        </div>
      </div>

      <!-- ===== اخبار مرتبط ===== -->
      <div class="section-box" v-if="news.length">
        <div class="section-title"><i class="fas fa-newspaper"></i> اخبار مرتبط</div>
        <div class="news-list">
          <article
              v-for="item in news"
              :key="item.id"
              class="news-row"
              @click="$router.push({ name: 'NewsDetail', params: { slug: item.slug } })"
          >
            <div class="news-thumb">
              <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title"
                   @error="e => e.target.style.display='none'"/>
              <i v-else class="fas fa-newspaper"></i>
            </div>
            <div class="news-text">
              <h4>{{ item.title }}</h4>
              <span class="news-meta">
                {{ item.source?.name }} · {{ timeAgo(item.publishedAt) }}
              </span>
            </div>
          </article>
        </div>
      </div>

      <!-- حالت خالی کامل -->
      <div v-if="!teamRecent.length && !news.length" class="empty-state">
        <i class="fas fa-info-circle"></i>
        <p>اطلاعات تکمیلی این مربی هنوز ثبت نشده</p>
      </div>

    </div>
  </div>
</template>

<script>
import apiService from '@/services/api'
import { teamNames, leagueNamesLatin, nationalityFa, coachNameFa } from '@/utils/translations'
import { setSeo } from '@/utils/seo'

export default {
  name: 'CoachDetail',

  data() {
    return {
      coach: null,
      nationality: null,
      flag: null,
      team: null,
      news: [],
      teamRecent: [],
      loading: true,
      error: null,
    }
  },

  computed: {
    faName() {
      return coachNameFa(this.coach?.name);
    },
    faCountry() {
      return nationalityFa[this.nationality] || this.nationality || '';
    },
    age() {
      if (!this.coach?.birthDate) return null;
      const d = new Date(this.coach.birthDate);
      if (isNaN(d.getTime())) return null;
      return Math.floor((Date.now() - d.getTime()) / (365.25 * 864e5));
    },
    avatarColor() {
      let h = 0;
      for (const ch of (this.coach?.name || '')) h = (h * 31 + ch.charCodeAt(0)) % 360;
      return `hsl(${h}, 40%, 30%)`;
    },
    photo() {
      // اولویت: عکس دیتابیس → فایل محلی src/assets/image/coach/{slug}.jpg|png → آیکون
      if (this.coach?.photo) return this.coach.photo;
      const slug = this.coach?.slug;
      if (!slug) return null;
      try { return require(`@/assets/image/coach/${slug}.jpg`); } catch (e) { /* فایل محلی نیست */ }
      try { return require(`@/assets/image/coach/${slug}.png`); } catch (e) { /* فایل محلی نیست */ }
      return null;
    },
  },

  methods: {
    async loadCoach() {
      this.loading = true;
      this.error = null;
      try {
        const key = this.$route.params.slug;
        const data = await apiService.request(`/coaches/${key}`);
        if (!data) {
          setSeo({ notFound: apiService.lastStatus === 404, title: 'مربی پیدا نشد | ضربه آزاد', path: `/coach/${key}` });
          this.error = 'مربی پیدا نشد'; return;
        }
        this.coach = data.coach;
        this.nationality = data.nationality;
        this.flag = data.flag;
        this.team = data.team;
        this.news = data.news || [];
        this.teamRecent = data.teamRecent || [];
        const cName = this.coach ? coachNameFa(this.coach.name) : '';
        if (cName) {
          setSeo({
            title: `${cName} | پروفایل سرمربی و اخبار | ضربه آزاد`,
            description: `${cName}${this.team ? '، سرمربی ' + (teamNames[this.team.name] || this.team.name) : ''}: پروفایل، تیم فعلی و آخرین اخبار مرتبط در ضربه آزاد`,
            image: (this.team && this.team.logo) || null,
            // canonical به نسخهٔ slug (اگر با id لود شده باشد)
            path: `/coach/${(this.coach && this.coach.slug) || key}`,
          });
        }
      } catch (e) {
        // فقط 404 واقعی noindex می‌گیرد؛ خطای سرور/شبکه URL سالم را noindex نمی‌کند
        setSeo({ notFound: apiService.lastStatus === 404, title: 'خطا در نمایش مربی | ضربه آزاد', path: `/coach/${this.$route.params.slug}` });
        this.error = 'خطا در دریافت اطلاعات مربی';
      } finally {
        this.loading = false;
      }
    },

    faTeamName(name) {
      return teamNames[name] || name || 'نامشخص';
    },
    faLeagueName(name) {
      return leagueNamesLatin[name] || name || '';
    },

    faNum(value) {
      if (value === null || value === undefined) return '—';
      return String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
    },

    faDate(dateStr) {
      if (!dateStr) return '—';
      try {
        return new Intl.DateTimeFormat(
            'fa-IR-u-ca-persian',
            { day: 'numeric', month: 'long', timeZone: 'Asia/Tehran' }
        ).format(new Date(dateStr));
      } catch (e) { return '—'; }
    },

    timeAgo(dateStr) {
      if (!dateStr) return '';
      const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
      if (diff < 3600) return `${Math.floor(diff / 60)} دقیقه پیش`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} ساعت پیش`;
      return `${Math.floor(diff / 86400)} روز پیش`;
    },
  },

  mounted() {
    this.loadCoach();
  },

  watch: {
    '$route.params.slug'() {
      this.loadCoach();
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/sass/variables';
@import '@/assets/sass/mixin';

.coach-page {
  width: 100%;
  min-height: 100vh;
  background: $gray-100;
  direction: rtl;
  font-family: $font-family;
}

.coach-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 24px 60px;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 120px 20px;
  color: $gray-500;

  &.error i { color: #dc2626; font-size: 2rem; }
}

.back-btn {
  background: $secondary-blue;
  color: #fff;
  padding: 8px 20px;
  border-radius: 8px;
  text-decoration: none;
}

.spinner {
  width: 42px; height: 42px;
  border: 4px solid $gray-200;
  border-top-color: $secondary-blue;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: $gray-500;
  margin-bottom: 14px;
  flex-wrap: wrap;

  a { color: $secondary-blue; text-decoration: none; &:hover { text-decoration: underline; } }
  i { font-size: 0.6rem; }
}

// ---------- hero ----------
.coach-hero {
  background: 
    url('~@/assets/images/header-ball-bg.svg') no-repeat left 4% center / 260px auto,
    linear-gradient(135deg, #022c22 0%, #064e3b 45%, #047857 85%, #059669 100%);
  border-radius: 14px;
  padding: 26px;
  color: #fff;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;

  @include mobile {
    background-position: left -30px center;
    background-size: 180px auto;
  }
}

.photo-wrap { flex-shrink: 0; }

.coach-photo {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.4);
  background: #fff;
}

.coach-avatar {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  color: #fff;
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid rgba(255, 255, 255, 0.4);
}

.hero-info { flex: 1; min-width: 200px; }

.coach-name {
  margin: 0 0 12px;
  font-size: 1.5rem;
}

.coach-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.85);

  .meta-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    i { font-size: 0.75rem; opacity: 0.8; }
  }
}

.flag {
  width: 18px;
  height: 13px;
  object-fit: cover;
  border-radius: 2px;
}

.team-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 10px 16px;
  color: #fff;
  text-decoration: none;
  font-weight: 600;

  img { width: 30px; height: 30px; object-fit: contain; }
  i { font-size: 0.7rem; opacity: 0.7; }
  &:hover { background: rgba(255, 255, 255, 0.2); }
}

// ---------- sections ----------
.section-box {
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: 14px;
  margin-bottom: 16px;
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 0.9rem;
  font-weight: 700;
  color: $gray-700;
  border-bottom: 1px solid $gray-100;

  i { color: $secondary-blue; }
}

// ---------- matches ----------
.matches-list { padding: 6px 0; }

.match-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 10px 18px;
  cursor: pointer;
  border-bottom: 1px solid $gray-50;
  transition: background 0.15s;

  &:hover { background: $gray-50; }
  &:last-child { border-bottom: none; }

  .comp {
    font-size: 0.68rem;
    color: $gray-400;
    background: $gray-100;
    border-radius: 6px;
    padding: 3px 8px;
    white-space: nowrap;
  }

  .teams {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 0.82rem;
    color: $gray-700;

    .t {
      flex: 1;
      min-width: 0;
      &:first-child { text-align: left; }
      &:last-child { text-align: right; }
      &.winner { color: #16a34a; font-weight: 700; }
    }
    .score { font-size: 0.95rem; white-space: nowrap; }
  }

  .date {
    font-size: 0.7rem;
    color: $gray-400;
    white-space: nowrap;
  }
}

// ---------- news ----------
.news-list { padding: 6px 0; }

.news-row {
  display: flex;
  gap: 12px;
  padding: 10px 18px;
  cursor: pointer;
  border-bottom: 1px solid $gray-50;
  transition: background 0.15s;

  &:hover { background: $gray-50; }
  &:last-child { border-bottom: none; }
}

.news-thumb {
  width: 72px;
  height: 48px;
  border-radius: 8px;
  background: $gray-100;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $gray-300;

  img { width: 100%; height: 100%; object-fit: cover; }
}

.news-text {
  min-width: 0;

  h4 {
    margin: 0 0 4px;
    font-size: 0.8rem;
    line-height: 1.6;
    color: $gray-700;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
}

.news-meta { font-size: 0.68rem; color: $gray-400; }

.empty-state {
  background: #fff;
  border: 1px dashed $gray-200;
  border-radius: 12px;
  padding: 48px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: $gray-400;

  i { font-size: 1.8rem; }
  p { margin: 0; font-size: 0.85rem; }
}

@media (max-width: 768px) {
  .coach-container { padding: 14px 14px 40px; }
  .coach-hero { padding: 18px; gap: 14px; }
  .coach-photo, .coach-avatar { width: 68px; height: 68px; font-size: 1.4rem; }
  .coach-name { font-size: 1.15rem; }
}
</style>
